import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { FileText, ArrowLeft, Calendar, DollarSign, Building2, Award } from 'lucide-react'
import { useApi } from '../../../shared/hooks/useApi'
import { truncateText, decodeHtmlEntities } from '../../../shared/utils/formatters'

interface ContractData {
  notice_id: string
  publication_date?: string
  order_object?: string
  part_number?: string
  contract_value?: number
  cpv_code?: string
}

const RelationshipDrillDown = () => {
  const { organizationName: encodedOrgName, contractorUuid } = useParams<{
    organizationName: string
    contractorUuid: string
  }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Keep raw org name for API equality, decode for display
  const organizationNameRaw = decodeURIComponent(encodedOrgName || '')
  const organizationName = decodeHtmlEntities(organizationNameRaw)
  // Get contractor name from query parameters and decode entities for display only
  const contractorName = decodeHtmlEntities(searchParams.get('contractorName') || '')

  // Get filters from URL parameters (year is optional, only filter if explicitly provided)
  const yearParam = searchParams.get('year')
  const year = yearParam ? parseInt(yearParam) : undefined
  const dateFrom = searchParams.get('date_from') || ''
  const dateTo = searchParams.get('date_to') || ''

  const { getOrganizationContractorContracts } = useApi()

  const [data, setData] = useState<ContractData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        setError(null)

        const result = await getOrganizationContractorContracts(
          organizationNameRaw,
          contractorUuid!,
          year,
          dateFrom,
          dateTo,
        )

        setData(result)
      } catch (err) {
        console.error('Failed to load relationship contracts:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    if (organizationName && contractorUuid) {
      loadData()
    }
  }, [organizationName, contractorUuid, year, dateFrom, dateTo, getOrganizationContractorContracts])

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-danger/5 border border-danger/20 rounded-md p-4">
        <p className="text-danger">Błąd podczas ładowania danych kontraktów: {error}</p>
      </div>
    )
  }

  const totalValue = data?.reduce((sum, item) => sum + (item.contract_value || 0), 0) || 0

  return (
    <div className="space-y-6">
      {/* Header with back buttons */}
      <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
        <div className="flex flex-col space-y-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(`/contractors/${contractorUuid!}`)}
              className="flex items-center space-x-2 text-ink-secondary hover:text-ink text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Powrót do {contractorName}</span>
            </button>
            <span className="text-ink-faint">|</span>
            <button
              onClick={() => navigate(`/organizations/${encodeURIComponent(organizationNameRaw)}`)}
              className="flex items-center space-x-2 text-ink-secondary hover:text-ink text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Powrót do {organizationName}</span>
            </button>
          </div>

          <div className="border-t border-border-subtle pt-4">
            <h2 className="text-xl font-semibold text-ink flex items-center flex-wrap gap-2">
              <Award className="h-6 w-6 text-success" />
              <span>{contractorName}</span>
              <span className="text-ink-faint mx-2">/</span>
              <Building2 className="h-6 w-6 text-brand" />
              <span>{organizationName}</span>
            </h2>
            <p className="text-sm text-ink-secondary mt-1">Lista wygranych postępowań</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
          <div className="flex items-center">
            <div className="p-2 bg-brand-bg rounded-lg">
              <FileText className="h-6 w-6 text-brand" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-secondary">Liczba kontraktów</p>
              <p className="text-2xl font-semibold text-ink">{data?.length || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
          <div className="flex items-center">
            <div className="p-2 bg-success/10 rounded-lg">
              <DollarSign className="h-6 w-6 text-success" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-secondary">Łączna wartość</p>
              <p className="text-xl font-semibold text-ink">
                {new Intl.NumberFormat('pl-PL', {
                  style: 'currency',
                  currency: 'PLN',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(totalValue)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contracts Table */}
      <div className="bg-surface-raised rounded-lg border border-border-subtle overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-lg font-medium text-ink">Kontrakty</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Data publikacji
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Przedmiot zamówienia
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Część
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Wartość kontraktu
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                  CPV
                </th>
              </tr>
            </thead>
            <tbody className="bg-surface-raised divide-y divide-border">
              {data?.map((item, index) => (
                <tr key={index} className="hover:bg-surface-hover">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-muted">
                    {item.publication_date
                      ? new Date(item.publication_date).toLocaleDateString('pl-PL')
                      : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => navigate(`/notices/${item.notice_id}`)}
                      className="text-sm font-medium text-brand hover:text-brand hover:underline text-left block w-full"
                    >
                      {truncateText(item.order_object || '', 100)}
                    </button>
                    <div className="text-xs text-ink-faint mt-1">ID: {item.notice_id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink text-right">
                    {item.part_number ? item.part_number : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-ink text-right">
                    {item.contract_value
                      ? new Intl.NumberFormat('pl-PL', {
                          style: 'currency',
                          currency: 'PLN',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }).format(item.contract_value)
                      : 'Brak danych'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {item.cpv_code && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-ai/10 text-ai">
                        {item.cpv_code.substring(0, 2)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(!data || data.length === 0) && (
          <div className="px-6 py-8 text-center text-ink-muted">
            <FileText className="h-12 w-12 mx-auto mb-4 text-ink-faint" />
            <p>Brak kontraktów dla tej pary w wybranym okresie</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default RelationshipDrillDown
